import { Avatar, type AvatarProps, Button, Divider, Menu, MenuItem, MenuTrigger, Text } from "@hopper-ui/components";
import { ExitIcon, NewTabIcon, QuestionIcon } from "@hopper-ui/icons";
import { navigateTo } from "@sg-protect/core";
import {
    type NavigationLinkRenderProps,
    type RenderItemFunction,
    isNavigationLink
} from "@sg-protect/federation-runtime";
import { useNavigationItems, useRenderedNavigationItems } from "@sg-protect/federation-runtime/squide";
import config from "@sg-protect/runtime-config";
import { useShellTranslation } from "../../../i18n/i18next.ts";
import { useSession } from "../../../SessionProvider.tsx";
import { ProtectUserMenuId } from "./constants.ts";

const renderItem: RenderItemFunction = (item, key) => {
    if (!isNavigationLink(item)) {
        return null;
    }

    return <NavigationMenuItem key={key} item={item} />;
};

interface NavigationMenuItemProps {
    item: NavigationLinkRenderProps;
}

function NavigationMenuItem({ item }: NavigationMenuItemProps) {
    return (
        <MenuItem href={item.linkProps.to as string}>
            {item.additionalProps.icon}
            <Text>{item.label}</Text>
        </MenuItem>
    );
}

function handleLogout() {
    navigateTo(config.get().insights.logout);
}

export function UserMenu() {
    const { t } = useShellTranslation();
    const navigationItems = useNavigationItems({ menuId: ProtectUserMenuId });

    const navigationElements = useRenderedNavigationItems(navigationItems, renderItem, el => el);

    return (
        <MenuTrigger>
            <Button
                variant="ghost-secondary"
                aria-label={t("UserMenu.openMenu")}
                data-telemetry="User menu"
                borderRadius="circle"
                backgroundColor="neutral-weak"
                backgroundColorHover="neutral-weak-hover"
                borderHover="none"
                border="none"
            >
                <CurrentUserAvatar size="md" color="neutral" backgroundColor="inherit" />
            </Button>
            <Menu size="xs" data-public>
                {navigationElements}

                <MenuItem onAction={handleLogout}>
                    <ExitIcon />
                    <Text>{t("UserMenu.logOut")}</Text>
                </MenuItem>

                <Divider />

                <MenuItem
                    href="https://documentation.sharegate.com/hc/en-us/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <QuestionIcon size="md" />
                    <Text>{t("UserMenu.getHelp")}</Text>
                </MenuItem>
                <MenuItem href="https://sharegate.com/terms" target="_blank" rel="noopener noreferrer">
                    <NewTabIcon size="md" />
                    <Text>{t("UserMenu.terms")}</Text>
                </MenuItem>
            </Menu>
        </MenuTrigger>
    );
}

interface CurrentUserAvatarProps {
    size?: AvatarProps["size"];
    width?: AvatarProps["width"];
    height?: AvatarProps["height"];
    color?: AvatarProps["color"];
    backgroundColor?: AvatarProps["backgroundColor"];
    backgroundColorHover?: AvatarProps["backgroundColorHover"];
}

function CurrentUserAvatar(props: CurrentUserAvatarProps) {
    const { userDisplayName } = useSession();

    return <Avatar name={userDisplayName} {...props} />;
}
